import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetDestinationsWithImageQuery } from "../../apiCaller/apis/getApi";
import { GetTableListAPI } from "../../apiCaller/getAPIsCaller";
import ClientLayout from "../../components/Layout/ClientLayout";
import PaginationComponent from "../../components/pagination";
import SpinningLoader from "../../components/SpinningLoader/SpinningLoader";
import { motion, AnimatePresence } from "framer-motion";
import { Mountain, MapPin, TrendingUp, ArrowRight, Calendar, Clock, Star, Sparkles, Search } from "lucide-react";
import NoImage from "../../assets/images/No_Image_Available.jpg";

const DestinationList = () => {
  const [countryLabel, setCountryLabel] = useState();
  const { country } = useParams();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideProgress, setSlideProgress] = useState(0);
  const slideDuration = 7000; // 7 seconds per slide
  const [search, setSearch] = useState({
    isTraverse: false,
    country: country,
    totalItems: 12,
  });

  // Update search when country parameter changes
  useEffect(() => {
    setSearch(prevSearch => ({
      ...prevSearch,
      country: country,
      page: 1  // Reset to page 1 when country changes
    }));
  }, [country]);
  const {
    data: destinations,
    error: destinationError,
    isLoading: destinationIsLoading,
    isFetching: destinationIsFetching,
  } = GetTableListAPI(
    search,
    useGetDestinationsWithImageQuery
  );

  const navigate = useNavigate();

  const handleClick = (slug) => {
    navigate(`/destinations/${slug}`);
  };

  
  useEffect(() => {
    switch (country) {
      case "ke":
        setCountryLabel("Kenyan");
        break;
      case "tz":
        setCountryLabel("Tanzanian");
        break;
      case "ug":
        setCountryLabel("Uganda");
        break;
      case "rw":
        setCountryLabel("Rwanda");
        break;
      default:
        setCountryLabel("All");
    }
  });

  const handlePageClick = (page) => {
    const newSearch = { ...search, page: page + 1 };
    setSearch(newSearch);
  };

  const handleSearch = (params) => {
    const newSearch = { ...search, ...params };
    setSearch(newSearch);
  };

  // Separate featured and regular destinations
  const featuredDestinations = destinations?.results?.filter(d => d.is_featured) || [];
  const regularDestinations = destinations?.results?.filter(d => !d.is_featured) || [];

  // Auto-advance featured slider
  useEffect(() => {
    if (featuredDestinations.length > 1) {
      const progressInterval = setInterval(() => {
        setSlideProgress((prev) => {
          if (prev >= 100) {
            setCurrentSlide((current) => (current + 1) % featuredDestinations.length);
            return 0;
          }
          return prev + (100 / (slideDuration / 50));
        });
      }, 50);
      return () => clearInterval(progressInterval);
    }
  }, [featuredDestinations.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setSlideProgress(0);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };
  return (
    <ClientLayout>
      {/* Hero Section - Mamba Theme */}
      <div className="px-5">
        <div className="relative h-[400px] lg:h-[500px] rounded-lg overflow-hidden">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80)`,
            }}
          />
          
          {/* Gradient Overlay - Mamba Theme */}
          <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/85 to-mamba-navy/90" />
          <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/95 via-transparent to-transparent" />

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -top-40 -right-40 w-96 h-96 bg-mamba-sky-blue/20 rounded-full blur-3xl"
            />
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
              className="absolute -bottom-40 -left-40 w-96 h-96 bg-mamba-golden/20 rounded-full blur-3xl"
            />
          </div>

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
            {/* Badge */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 mb-6 px-5 py-2.5 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full"
            >
              <Mountain className="w-5 h-5 text-mamba-golden" />
              <span className="text-sm text-white font-montserrat font-bold tracking-wide uppercase">
                Explore Destinations
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-montserrat font-black text-white mb-6 tracking-tight leading-none"
            >
              {countryLabel}
              <span className="block bg-gradient-to-r from-mamba-golden via-mamba-sky-blue to-mamba-sky-blue bg-clip-text text-transparent">
                Destinations
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto leading-relaxed font-inter"
            >
              Discover breathtaking trails across East Africa with <span className="font-dancing italic text-mamba-golden">Mamba Hikers</span>
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="relative py-16 px-5 md:px-10 lg:px-32 xl:px-48 bg-gradient-to-b from-white via-mamba-bg-light to-white">
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <DestinationSearchBarRedesign handleSearch={handleSearch} />
        </motion.div>

        {destinationIsLoading || destinationIsFetching ? (
          <div className="min-h-[400px] flex items-center justify-center">
            <SpinningLoader isLoading={true} hasData={false} />
          </div>
        ) : (
          <>
            {/* Featured Destinations Slider */}
            {featuredDestinations.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-20"
              >
                {/* Section Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="h-px w-12 bg-gradient-to-r from-mamba-golden to-transparent" />
                  <Sparkles className="w-6 h-6 text-mamba-golden" />
                  <h2 className="text-3xl lg:text-4xl font-montserrat font-black text-mamba-navy tracking-tight">
                    Featured Destinations
                  </h2>
                </div>

                {/* Featured Slider */}
                <div className="relative rounded-3xl overflow-hidden">
                  {/* Background for current slide */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentSlide}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1 }}
                      className="absolute inset-0 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${featuredDestinations[currentSlide]?.images?.[0]?.image_url || NoImage})`,
                      }}
                    />
                  </AnimatePresence>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-mamba-navy/95 via-mamba-navy/80 to-transparent" />

                  {/* Content */}
                  <div className="relative z-10 px-8 md:px-16 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
                    {/* Left: Info */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`content-${currentSlide}`}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 30 }}
                        transition={{ duration: 0.6 }}
                      >
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full">
                          <Star className="w-4 h-4 text-mamba-golden fill-mamba-golden" />
                          <span className="text-xs text-mamba-golden font-montserrat font-bold uppercase">Featured</span>
                        </div>

                        {/* Title */}
                        <h3 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-black text-white mb-4 leading-tight">
                          {featuredDestinations[currentSlide]?.name}
                        </h3>

                        {/* Location */}
                        <div className="flex items-center gap-2 mb-6">
                          <MapPin className="w-5 h-5 text-mamba-golden" />
                          <span className="text-lg text-slate-200 font-medium">
                            {featuredDestinations[currentSlide]?.country}
                          </span>
                        </div>

                        {/* Description */}
                        <p className="text-lg text-slate-200 mb-8 leading-relaxed font-inter line-clamp-4">
                          {featuredDestinations[currentSlide]?.description_no_tags || featuredDestinations[currentSlide]?.description}
                        </p>

                        {/* CTA Button */}
                        <motion.button
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleClick(featuredDestinations[currentSlide]?.slug)}
                          className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue text-white font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-mamba-golden/50 transition-all duration-300"
                        >
                          Explore Details
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                      </motion.div>
                    </AnimatePresence>

                    {/* Right: Stats/Info Cards */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`stats-${currentSlide}`}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -30 }}
                        transition={{ duration: 0.6 }}
                        className="grid grid-cols-2 gap-4"
                      >
                        {[
                          { label: "Difficulty", value: featuredDestinations[currentSlide]?.difficulty || "Moderate", icon: TrendingUp },
                          { label: "Distance", value: `${featuredDestinations[currentSlide]?.distance_in_km || "-"} KM`, icon: Mountain },
                          { label: "Type", value: featuredDestinations[currentSlide]?.type || "Mountain", icon: MapPin },
                          { label: "Elevation", value: `${featuredDestinations[currentSlide]?.max_elevation || "-"}M`, icon: TrendingUp },
                        ].map((stat, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="backdrop-blur-md bg-white/10 rounded-2xl border-2 border-mamba-golden/30 p-6"
                          >
                            <stat.icon className="w-8 h-8 text-mamba-golden mb-3" />
                            <div className="text-2xl font-montserrat font-black text-white mb-1">
                              {stat.value}
                            </div>
                            <div className="text-sm text-slate-300">{stat.label}</div>
                          </motion.div>
                        ))}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Slider Navigation Dots */}
                  {featuredDestinations.length > 1 && (
                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex justify-center gap-3">
                      {featuredDestinations.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => goToSlide(index)}
                          className="group relative"
                        >
                          <div className={`h-1 rounded-full transition-all duration-300 ${
                            index === currentSlide ? 'w-12 bg-mamba-golden' : 'w-8 bg-white/40 group-hover:bg-white/60'
                          }`}>
                            {index === currentSlide && (
                              <motion.div
                                className="h-full bg-gradient-to-r from-mamba-golden to-mamba-sky-blue rounded-full"
                                initial={{ width: '0%' }}
                                animate={{ width: `${slideProgress}%` }}
                                transition={{ duration: 0.05 }}
                              />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Regular Destinations Grid */}
            {regularDestinations.length > 0 && (
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                {/* Section Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="h-px w-12 bg-gradient-to-r from-mamba-sky-blue to-transparent" />
                  <h2 className="text-3xl lg:text-4xl font-montserrat font-black text-mamba-navy tracking-tight">
                    All Destinations
                  </h2>
                </div>

                {/* Destinations Grid with Banners */}
                <div className="space-y-12">
                  {regularDestinations.map((destination, index) => (
                    <React.Fragment key={destination.id}>
                      {/* Show grid items */}
                      {index % 6 === 0 && (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {regularDestinations.slice(index, index + 6).map((dest) => (
                            <motion.div
                              key={dest.id}
                              variants={fadeInUp}
                              whileHover={{ y: -12 }}
                              onClick={() => handleClick(dest.slug)}
                              className="group relative bg-gradient-to-br from-white to-slate-50 rounded-3xl overflow-hidden border-2 border-mamba-sky-blue/20 hover:border-mamba-golden transition-all duration-500 hover:shadow-2xl cursor-pointer"
                            >
                              {/* Image */}
                              <div className="relative h-72 overflow-hidden">
                                <motion.img
                                  whileHover={{ scale: 1.1 }}
                                  transition={{ duration: 0.6 }}
                                  src={dest.images?.[0]?.image_url || NoImage}
                                  alt={dest.name}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />

                                {/* Type Badge */}
                                <div className="absolute top-4 right-4">
                                  <div className="px-3 py-1.5 bg-mamba-golden/90 backdrop-blur-sm text-white text-xs font-bold rounded-full">
                                    {dest.type || "Mountain"}
                                  </div>
                                </div>

                                {/* Difficulty Badge */}
                                <div className="absolute top-4 left-4">
                                  <div className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-mamba-navy text-xs font-bold rounded-full">
                                    {dest.difficulty || "Moderate"}
                                  </div>
                                </div>
                              </div>

                              {/* Content */}
                              <div className="p-6">
                                <div className="flex items-center gap-2 text-mamba-navy text-sm font-medium mb-2">
                                  <MapPin className="w-4 h-4 text-mamba-sky-blue" />
                                  {dest.country}
                                </div>
                                <h3 className="text-2xl font-montserrat font-black text-mamba-navy mb-3 leading-tight group-hover:text-mamba-sky-blue transition-colors">
                                  {dest.name}
                                </h3>
                                <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3 font-inter">
                                  {dest.description_no_tags || dest.description}
                                </p>

                                {/* Stats row */}
                                <div className="flex items-center justify-between pt-4 border-t border-mamba-sky-blue/20">
                                  {dest.distance_in_km && (
                                    <div>
                                      <div className="text-xs text-slate-500 mb-1">
                                        Distance
                                      </div>
                                      <div className="text-xl font-montserrat font-black text-mamba-navy">
                                        {dest.distance_in_km} KM
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 text-mamba-sky-blue font-semibold text-sm group-hover:gap-3 transition-all">
                                    Explore
                                    <ArrowRight className="w-4 h-4" />
                                  </div>
                                </div>
                              </div>

                              {/* Accent gradient at bottom */}
                              <div className="h-1 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                            </motion.div>
                          ))}
                        </div>
                      )}

                      {/* Decorative Banner - Show after every 6 destinations */}
                      {(index + 1) % 6 === 0 && index < regularDestinations.length - 1 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8 }}
                          className="my-12"
                        >
                          <div className="relative rounded-3xl overflow-hidden">
                            {/* Background Image */}
                            <div
                              className="absolute inset-0 bg-cover bg-center"
                              style={{
                                backgroundImage: `url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80)`,
                              }}
                            />
                            
                            {/* Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-r from-mamba-navy/95 via-mamba-navy/85 to-mamba-sky-blue/90" />
                            
                            {/* Animated Dots Pattern */}
                            <div className="absolute inset-0 opacity-20">
                              <div
                                className="absolute inset-0"
                                style={{
                                  backgroundImage: `radial-gradient(circle at 2px 2px, rgb(212 165 116 / 0.3) 1px, transparent 0)`,
                                  backgroundSize: "32px 32px",
                                }}
                              />
                            </div>

                            {/* Animated Background Elements */}
                            <motion.div
                              animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.1, 0.2, 0.1],
                              }}
                              transition={{
                                duration: 8,
                                repeat: Infinity,
                                ease: "easeInOut",
                              }}
                              className="absolute -right-20 top-1/2 -translate-y-1/2 w-64 h-64 bg-mamba-golden/30 rounded-full blur-3xl"
                            />

                            {/* Content */}
                            <div className="relative z-10 px-8 py-12 md:py-16 flex flex-col md:flex-row items-center justify-between gap-8">
                              {/* Left Side - Icon & Text */}
                              <div className="flex items-center gap-6 flex-1">
                                <motion.div
                                  animate={{
                                    rotate: [0, 5, -5, 0],
                                  }}
                                  transition={{
                                    duration: 6,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                  }}
                                  className="flex-shrink-0"
                                >
                                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-mamba-golden to-mamba-sky-blue p-1 shadow-2xl">
                                    <div className="w-full h-full rounded-full bg-mamba-navy flex items-center justify-center">
                                      <Mountain className="w-10 h-10 md:w-12 md:h-12 text-mamba-golden" />
                                    </div>
                                  </div>
                                </motion.div>

                                <div>
                                  <motion.h3
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="text-2xl md:text-4xl font-dancing italic text-mamba-golden mb-2"
                                  >
                                    More Destinations Await
                                  </motion.h3>
                                  <motion.p
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.3 }}
                                    className="text-base md:text-lg text-white/90 font-inter"
                                  >
                                    Keep scrolling to discover more breathtaking trails
                                  </motion.p>
                                </div>
                              </div>

                              {/* Right Side - Animated Trail Icon */}
                              <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                className="flex items-center gap-3"
                              >
                                {[0, 1, 2].map((idx) => (
                                  <motion.div
                                    key={idx}
                                    animate={{
                                      y: [0, -10, 0],
                                    }}
                                    transition={{
                                      duration: 2,
                                      repeat: Infinity,
                                      delay: idx * 0.2,
                                      ease: "easeInOut",
                                    }}
                                    className="w-3 h-3 rounded-full bg-gradient-to-br from-mamba-golden to-mamba-sky-blue shadow-lg"
                                  />
                                ))}
                              </motion.div>
                            </div>

                            {/* Bottom Accent Line */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-mamba-golden via-mamba-sky-blue to-mamba-golden" />
                          </div>
                        </motion.div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </motion.div>
            )}

            {/* No Results */}
            {!featuredDestinations.length && !regularDestinations.length && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="text-center py-20"
              >
                <div className="inline-flex p-8 bg-mamba-bg-light rounded-full mb-6 border-2 border-mamba-sky-blue/20">
                  <Mountain className="w-20 h-20 text-mamba-navy" />
                </div>
                <h3 className="text-3xl font-montserrat font-bold text-mamba-navy mb-3">
                  No Destinations Found
                </h3>
                <p className="text-slate-600 text-lg max-w-md mx-auto font-inter">
                  Try adjusting your search filters to explore more options.
                </p>
              </motion.div>
            )}

            {/* Pagination */}
            {/* {(featuredDestinations.length > 0 || regularDestinations.length > 0) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-16"
              >
                <PaginationComponent
                  pageCount={destinations?.totalPages}
                  totalItems={destinations?.totalItems}
                  handlePageClick={handlePageClick}
                />
              </motion.div>
            )} */}
          </>
        )}
      </section>
    </ClientLayout>
  );
};

// Destination Search Bar Component - Mamba Theme
const DestinationSearchBarRedesign = ({ handleSearch }) => {
  const [q, setQ] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [country, setCountry] = useState("");

  const handleSearchClicked = () => {
    let search = {};
    if (q !== null && q !== undefined && q !== "") {
      search["q"] = q;
    }
    if (difficulty !== null && difficulty !== undefined && difficulty !== "") {
      search["difficulty"] = difficulty;
    }
    if (country !== null && country !== undefined && country !== "") {
      search["country"] = country;
    }
    return handleSearch(search);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative bg-white rounded-3xl border-2 border-mamba-sky-blue/20 shadow-lg hover:shadow-2xl hover:border-mamba-golden transition-all duration-300 overflow-hidden"
    >
      {/* Decorative top accent - Mamba Theme */}
      <div className="h-1 bg-gradient-to-r from-mamba-navy via-mamba-sky-blue to-mamba-golden" />

      <div className="p-8">
        {/* Header - Mamba Theme */}
        <div className="mb-6">
          <h3 className="text-2xl font-montserrat font-black text-mamba-navy tracking-tight mb-2">
            Find Your Perfect Trail
          </h3>
          <p className="text-slate-600 text-sm font-inter">
            Search by name, filter by difficulty, or explore by country
          </p>
        </div>

        {/* Search Input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400">
            <Search className="w-5 h-5" />
          </div>
          <input
            type="text"
            name="q"
            placeholder="Search destinations by name..."
            className="w-full pl-12 pr-4 py-4 border-2 border-mamba-sky-blue/30 rounded-2xl focus:outline-none focus:border-mamba-sky-blue focus:ring-4 focus:ring-mamba-sky-blue/20 transition-all duration-300 text-mamba-navy placeholder:text-slate-400"
            onChange={(text) => setQ(text?.target?.value)}
            value={q}
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Difficulty Filter */}
          <div className="relative">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Difficulty Level
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400">
                <TrendingUp className="w-5 h-5" />
              </div>
              <select
                name="difficulty"
                className="w-full pl-12 pr-4 py-4 border-2 border-mamba-sky-blue/30 rounded-2xl focus:outline-none focus:border-mamba-sky-blue focus:ring-4 focus:ring-mamba-sky-blue/20 transition-all duration-300 text-mamba-navy appearance-none bg-white cursor-pointer"
                onChange={(e) => setDifficulty(e?.target?.value)}
                value={difficulty}
              >
                <option value="">All Difficulties</option>
                <option value="Beginner Friendly">Beginner Friendly</option>
                <option value="Moderate">Moderate</option>
                <option value="Challenging">Challenging</option>
                <option value="Extreme">Extreme</option>
              </select>
            </div>
          </div>

          {/* Country Filter */}
          <div className="relative">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Country
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400">
                <MapPin className="w-5 h-5" />
              </div>
              <select
                name="country"
                className="w-full pl-12 pr-4 py-4 border-2 border-mamba-sky-blue/30 rounded-2xl focus:outline-none focus:border-mamba-golden focus:ring-4 focus:ring-mamba-golden/20 transition-all duration-300 text-mamba-navy appearance-none bg-white cursor-pointer"
                onChange={(e) => setCountry(e?.target?.value)}
                value={country}
              >
                <option value="">All Countries</option>
                <option value="Kenya">Kenya</option>
                <option value="Tanzania">Tanzania</option>
                <option value="Uganda">Uganda</option>
                <option value="Rwanda">Rwanda</option>
              </select>
            </div>
          </div>
        </div>

        {/* Search Button - Mamba Theme */}
        <motion.button
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          className="w-full group relative px-6 py-4 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue text-white font-montserrat font-bold rounded-2xl shadow-lg hover:shadow-mamba-sky-blue/50 transition-all duration-300 overflow-hidden"
          onClick={handleSearchClicked}
        >
          {/* Shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

          <span className="relative flex items-center justify-center gap-2">
            <Search className="w-5 h-5" />
            Search Destinations
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </span>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default DestinationList;
